import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import ConnectionHeadersAttributes from '../Components/ConnectionAttributes';
import ConnectionParameterAttributes from '../Components/ConnectionParameterAttributes';
import { CustomInput, CustomSelect, CustomToggle } from '../Components/CustomFormElements';
import PageLoader from '../Components/PageLoader';
import { intercomRegions } from '../constants/intercomRegions';
import { AgentApiProvider, useAgentApi } from '../Services/AgentApiProvider';
import { ConnectionApiProvider, useConnectionApi } from '../Services/ConnectionApiProvider';
import {
  hasValidNameFormat,
  isEmpty,
  isValidEmail,
  isValidPassword,
  isValidPort,
  isValidServerAddress,
} from '../utils/validationUtil';
import ConnectionObjectsList from './ConnectionObjectsList';
import { ConnectionObjectApiProvider } from '../Services/ConnectionObjectApiProvider';

function ConnectionDetail({ canEdit }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationId, connectionId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const {
    createConnection,
    updateConnectionById,
    getConnectionById,
    getConnectionSecretsById,
    getGoogleAccountConstants,
    disconnectGoogleAccount,
    getZohoAccountConstants,
    disconnectZohoAccount,
  } = useConnectionApi();

  const { getAgents } = useAgentApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isNew, setIsNew] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);
  const [activeTab, setActiveTab] = useState(
    location.pathname.includes('/objects') ? 'view-objects' : 'settings',
  );

  const [connectionName, setConnectionName] = useState();
  const [isActive, setIsActive] = useState(true);
  const [connectionType, setConnectionType] = useState();
  const [connectionSecrets, setConnectionSecrets] = useState({});
  const [zendeskDomain, setZendeskDomain] = useState();
  const [intercomRegion, setIntercomRegion] = useState('us');
  const [creatioBaseUrl, setCreatioBaseUrl] = useState();
  const [creatioTokenUrl, setCreatioTokenUrl] = useState();
  const [creatioScope, setCreatioScope] = useState();
  const [keyCrmBaseUrl, setKeyCrmBaseUrl] = useState();
  const [mcpBaseUrl, setMcpBaseUrl] = useState();
  const [apiBaseUrl, setApiBaseUrl] = useState();
  const [notionWorkspaceId, setNotionWorkspaceId] = useState();
  const [headerAttributes, setHeaderAttributes] = useState([]);
  const [parameterAttributes, setParameterAttributes] = useState([]);
  const [autoMcpToolSync, setAutoMcpToolSync] = useState(true);

  const [googleAuthUrl, setGoogleAuthUrl] = useState();
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);

  const [smtpAddress, setSmtpAddress] = useState();
  const [smtpPort, setSmtpPort] = useState();
  const [imapAddress, setImapAddress] = useState();
  const [imapPort, setImapPort] = useState();
  const [email, setEmail] = useState();

  const [jiraBaseUrl, setJiraBaseUrl] = useState();

  const [agents, setAgents] = useState([]);
  const [assistantKey, setAssistantKey] = useState('');

  const [zohoAuthUrl, setZohoAuthUrl] = useState();
  const [isZohoConnected, setIsZohoConnected] = useState(false);

  const [uspacyBaseUrl, setUspacyBaseUrl] = useState();
  const [uspacyEmail, setUspacyEmail] = useState('');

  const updateSecrets = (newSecrets) => {
    setConnectionSecrets((prevSecrets) => ({
      ...prevSecrets,
      ...newSecrets,
    }));
  };

  useEffect(() => {
    if (location.pathname.includes('/objects')) {
      setActiveTab('view-objects');
    } else {
      setActiveTab('settings');
    }
  }, [location.pathname]);

  useEffect(() => {
    const func = async () => {
      setLoading(true);
      try {
        const agentsResponse = await getAgents(0, 100);
        setAgents(agentsResponse?.items || []);

        if (connectionId !== 'new-connection') {
          const response = await getConnectionById(connectionId);
          const secrets = await getConnectionSecretsById(connectionId);
          setConnectionSecrets(secrets);
          setConnectionName(response.name);
          setZendeskDomain(response.zendesk_domain);
          setIntercomRegion(response.intercom_region || 'us');
          setCreatioTokenUrl(response.creatio_token_url);
          setCreatioBaseUrl(response.creatio_base_url);
          setKeyCrmBaseUrl(response.key_crm_base_url);
          setMcpBaseUrl(response.mcp_base_url);
          setApiBaseUrl(response.api_base_url);
          setCreatioScope(response.creatio_scope);
          setIsActive(response.is_active);
          setConnectionType(response.type);
          setNotionWorkspaceId(response.notion_workspace_id);
          setIsGoogleConnected(response.is_google_connected);
          setHeaderAttributes(response.header_attributes || []);
          setParameterAttributes(response.parameter_attributes || []);
          setSmtpAddress(response.smtp_address);
          setSmtpPort(response.smtp_port);
          setImapAddress(response.imap_address);
          setImapPort(response.imap_port);
          setEmail(response.email);
          setJiraBaseUrl(response.jira_base_url);
          setAssistantKey(response.assistant_key || '');
          setIsZohoConnected(response.is_zoho_connected);
          setUspacyBaseUrl(response.uspacy_base_url);
          setUspacyEmail(response.uspacy_email || '');
          setAutoMcpToolSync(response.auto_mcp_tool_sync);

          if (['GOOGLE_DRIVE', 'GOOGLE_CALENDAR'].includes(response.type)) {
            try {
              const constants = await getGoogleAccountConstants(connectionId, response.type);
              setGoogleAuthUrl(constants.google_auth_url);
            } catch (err) {
              console.error('Error fetching Google account constants:', err);
            }
          }

          if (response.type === 'ZOHO') {
            try {
              const constants = await getZohoAccountConstants(connectionId);
              setZohoAuthUrl(constants.zoho_auth_url);
            } catch (err) {
              console.error('Error fetching Zoho account constants:', err);
            }
          }
        }
      } catch (error) {
        setError('Error fetching connection data');
        console.error('Error fetching connection data:', error);
      } finally {
        setLoading(false);
      }
    };
    func();
  }, [connectionId]);

  useEffect(() => {
    setIsNew(connectionId === 'new-connection');
    setIsDisabled(!canEdit);
  }, [connectionId]);

  const onSave = async () => {
    if (isEmpty(connectionName)) {
      showAlert('fail', 'Connection name is required');
      return;
    }
    if (isEmpty(connectionType)) {
      showAlert('fail', 'Connection type is required');
      return;
    }
    if (!hasValidNameFormat(connectionName)) {
      showAlert(
        'fail',
        'Connection name can only contain letters, numbers, spaces, underscores, and hyphens',
      );
      return;
    }

    if (connectionType === 'ZENDESK') {
      if (isEmpty(zendeskDomain)) {
        showAlert('fail', 'Zendesk domain is required');
        return;
      }
    }

    if (connectionType === 'INTERCOM') {
      if (isEmpty(connectionSecrets?.intercom_access_token)) {
        showAlert('fail', 'Intercom Access Token is required');
        return;
      }
      if (isEmpty(connectionSecrets?.intercom_client_id)) {
        showAlert('fail', 'Intercom Client ID is required');
        return;
      }
      if (isEmpty(connectionSecrets?.intercom_client_secret)) {
        showAlert('fail', 'Intercom Client Secret is required');
        return;
      }
    }

    if (connectionType === 'TRELLO') {
      if (isEmpty(connectionSecrets?.trello_api_key)) {
        showAlert('fail', 'Trello API Key is required');
        return;
      }
      if (isEmpty(connectionSecrets?.trello_api_token)) {
        showAlert('fail', 'Trello API Token is required');
        return;
      }
    }

    if (connectionType === 'JIRA') {
      if (isEmpty(jiraBaseUrl)) {
        showAlert('fail', 'Jira Base URL is required');
        return;
      }
      if (isEmpty(connectionSecrets?.jira_email)) {
        showAlert('fail', 'Jira Email is required');
        return;
      }
      if (!isValidEmail(connectionSecrets?.jira_email)) {
        showAlert('fail', 'Jira Email must be in format like name@example.com');
        return;
      }
      if (isEmpty(connectionSecrets?.jira_api_token)) {
        showAlert('fail', 'Jira API Token is required');
        return;
      }
    }

    if (connectionType === 'ZOHO') {
      if (isEmpty(connectionSecrets?.zoho_client_id)) {
        showAlert('fail', 'Zoho Client ID is required');
        return;
      }
      if (isEmpty(connectionSecrets?.zoho_client_secret)) {
        showAlert('fail', 'Zoho Client Secret is required');
        return;
      }
    }

    if (connectionType === 'USPACY') {
      if (isEmpty(uspacyBaseUrl)) {
        showAlert('fail', 'USPACY Portal URL is required');
        return;
      }
      if (!isValidEmail(uspacyEmail)) {
        showAlert('fail', 'Enter a valid USPACY email');
        return;
      }
      if (isEmpty(connectionSecrets?.uspacy_password)) {
        showAlert('fail', 'USPACY password is required');
        return;
      }
    }

    if (connectionType === 'LIQPAY') {
      if (isEmpty(connectionSecrets?.liqpay_public_key)) {
        showAlert('fail', 'LiqPay Public Key is required');
        return;
      }
      if (isEmpty(connectionSecrets?.liqpay_private_key)) {
        showAlert('fail', 'LiqPay Private Key is required');
        return;
      }
    }

    if (connectionType === 'CREATIO') {
      if (isEmpty(creatioBaseUrl)) {
        showAlert('fail', 'Creatio base url is required');
        return;
      }
      if (isEmpty(creatioTokenUrl)) {
        showAlert('fail', 'Creatio token url is required');
        return;
      }
    }

    if (connectionType === 'NOTION') {
      if (isEmpty(notionWorkspaceId)) {
        showAlert('fail', 'Notion Workspace Id is required');
        return;
      }

      if (isEmpty(connectionSecrets?.notion_access_token)) {
        showAlert('fail', 'Notion Access Token is required');
        return;
      }
    }

    if (connectionType === 'EMAIL') {
      if (isEmpty(smtpAddress)) {
        showAlert('fail', 'SMTP Address is required');
        return;
      }
      if (isEmpty(smtpPort)) {
        showAlert('fail', 'SMTP Port is required');
        return;
      }
      if (isEmpty(imapAddress)) {
        showAlert('fail', 'IMAP Address is required');
        return;
      }
      if (isEmpty(imapPort)) {
        showAlert('fail', 'IMAP Port is required');
        return;
      }
      if (isEmpty(email)) {
        showAlert('fail', 'Email is required');
        return;
      }
      if (isEmpty(connectionSecrets?.email_password)) {
        showAlert('fail', 'Password is required');
        return;
      }

      if (!isValidServerAddress(smtpAddress)) {
        showAlert(
          'fail',
          'SMTP address must be 1–50 chars and contain only letters, numbers, dots or hyphens',
        );
        return;
      }
      if (!isValidPort(smtpPort)) {
        showAlert('fail', 'SMTP port must be a number between 1 and 9999');
        return;
      }
      if (!isValidServerAddress(imapAddress)) {
        showAlert(
          'fail',
          'IMAP address must be 1–50 chars and contain only letters, numbers, dots or hyphens',
        );
        return;
      }
      if (!isValidPort(imapPort)) {
        showAlert('fail', 'IMAP port must be a number between 1 and 9999');
        return;
      }
      if (!isValidEmail(email)) {
        showAlert('fail', 'Email must be in format like name@example.com');
        return;
      }
      if (!isValidPassword(connectionSecrets?.email_password)) {
        showAlert('fail', 'Password must be between 1 and 50 characters');
        return;
      }
    }

    let connection = {
      name: connectionName,
      type: connectionType,
      is_active: isActive,
      zendesk_domain: zendeskDomain,
      creatio_base_url: creatioBaseUrl,
      creatio_token_url: creatioTokenUrl,
      creatio_scope: creatioScope,
      key_crm_base_url: keyCrmBaseUrl,
      notion_workspace_id: notionWorkspaceId,
      mcp_base_url: mcpBaseUrl,
      api_base_url: apiBaseUrl,
      smtp_address: smtpAddress,
      smtp_port: smtpPort,
      imap_address: imapAddress,
      imap_port: imapPort,
      email: email,
      jira_base_url: jiraBaseUrl,
      uspacy_base_url: uspacyBaseUrl,
      uspacy_email: uspacyEmail,
      assistant_key: connectionType === 'A2A' ? assistantKey || null : null,
      auto_mcp_tool_sync: autoMcpToolSync,
    };

    if (connectionType === 'INTERCOM') {
      connection.intercom_region = intercomRegion;
    }

    connection = {
      ...connection,
      ...connectionSecrets,
    };

    if (connectionType === 'MCP') {
      connection.header_attributes = headerAttributes;
      connection.parameter_attributes = parameterAttributes;
    }

    if (connectionType === 'API') {
      connection.header_attributes = headerAttributes;
    }

    setLoading(true);

    try {
      if (isNew) {
        await createConnection(connection);
        showAlert('success', 'Connection created successfully');
      } else {
        await updateConnectionById(connectionId, connection);
        showAlert('success', 'Connection updated successfully');
      }

      navigate(`/organizations/${organizationId}/connections`);
    } catch (error) {
      console.error('Error saving connection:', error);
      showAlert('fail', 'Failed to save connection');
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/connections`);
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <div className="d-flex justify-between align-center">
            <h2 className="h2-title">{connectionName}</h2>
            {!isNew && (
              <div className="buttons-container d-flex justify-end">
                <Button
                  variant={activeTab === 'view-objects' ? 'primary' : 'secondary'}
                  size="large"
                  onClick={() => {
                    if (activeTab === 'view-objects') {
                      navigate(`/organizations/${organizationId}/connections/${connectionId}`);
                    } else {
                      navigate(
                        `/organizations/${organizationId}/connections/${connectionId}/objects`,
                      );
                    }
                  }}
                >
                  {activeTab === 'view-objects' ? 'View Settings' : 'View Connection Objects'}
                </Button>
              </div>
            )}
          </div>
          <div className="gap d-flex flex-row-to-col flex-column">
            {(activeTab === 'settings' || isNew) && (
              <>
                <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
                  <h4 className="h4-title">Main Settings</h4>

                  <div className="input-block d-flex flex-row justify-between">
                    <div className="input-description">
                      <div className="input-title">Connection name</div>
                      <div className="small-font">Enter connection name</div>
                    </div>
                    <div className="align-wrapper d-flex align-center">
                      <CustomInput
                        id="chat-name"
                        label="Connection Name"
                        placeholder="Enter connection name"
                        type="text"
                        icon={<i className="fa-solid fa-comments"></i>}
                        value={connectionName}
                        onChange={(id, value) => setConnectionName(value)}
                        disabled={isDisabled}
                      />
                    </div>
                  </div>

                  <div className="input-row input-block d-flex flex-row justify-between">
                    <div className="input-description">
                      <div className="input-title">Connection Type</div>
                      <div className="small-font">Select connection type</div>
                    </div>
                    <div className="align-wrapper d-flex align-center">
                      <CustomSelect
                        id="connection-type"
                        label="Connection Type"
                        options={[
                          { value: 'NOTION', label: 'Notion' },
                          { value: 'KEYCRM', label: 'CRM - KeyCRM' },
                          { value: 'CREATIO', label: 'CRM - Creatio' },
                          { value: 'JIRA', label: 'Jira' },
                          { value: 'TRELLO', label: 'Trello' },
                          { value: 'ZOHO', label: 'Zoho' },
                          { value: 'USPACY', label: 'Uspacy' },
                          { value: 'LIQPAY', label: 'LiqPay' },
                          { value: 'ZENDESK', label: 'Zendesk' },
                          { value: 'INTERCOM', label: 'Intercom' },
                          { value: 'A2A', label: 'A2A' },
                          { value: 'GOOGLE_DRIVE', label: 'Google Drive' },
                          { value: 'GOOGLE_CALENDAR', label: 'Google Calendar' },
                          { value: 'MCP', label: 'MCP' },
                          { value: 'API', label: 'API' },
                          { value: 'EMAIL', label: 'Email' },
                        ]}
                        value={connectionType}
                        onChange={(value) => {
                          setConnectionType(value);
                        }}
                        multiSelect={false}
                        disabled={isDisabled || !isNew}
                      />
                    </div>
                  </div>
                  {connectionType === 'NOTION' && (
                    <>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Notion Workspace Id</div>
                          <div className="small-font">Notion Workspace Id</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="notion-workspace-id"
                            label="Notion Workspace Id"
                            placeholder="Enter workspace id"
                            icon={<i className="fa-brands fa-telegram"></i>}
                            value={notionWorkspaceId}
                            onChange={(id, value) => setNotionWorkspaceId(value)}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Notion Access Token</div>
                          <div className="small-font">Notion Access Token</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="notion-access-token"
                            label="Notion Access Token"
                            placeholder="Enter token"
                            icon={<i className="fa-brands fa-telegram"></i>}
                            value={connectionSecrets?.notion_access_token}
                            onChange={(id, value) => updateSecrets({ notion_access_token: value })}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {connectionType === 'ZENDESK' && (
                    <>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Zendesk Domain</div>
                          <div className="small-font">Enter Zendesk Domain</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="zendesk-domain"
                            label="Zendesk Domain"
                            placeholder="Enter domain"
                            icon={<i className="fa-brands fa-telegram"></i>}
                            value={zendeskDomain}
                            onChange={(id, value) => setZendeskDomain(value)}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Zendesk Ticket Email Key</div>
                          <div className="small-font">Enter Zendesk Ticket Email Key</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="zendesk-ticket-email-key"
                            label="Zendesk Ticket Email Key"
                            placeholder="Enter token"
                            icon={<i className="fa-brands fa-telegram"></i>}
                            value={connectionSecrets?.zendesk_ticket_key_email}
                            onChange={(id, value) =>
                              updateSecrets({ zendesk_ticket_key_email: value })
                            }
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Zendesk Ticket Key</div>
                          <div className="small-font">Enter Zendesk Ticket Key</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="zendesk-ticket-key"
                            label="Zendesk Ticket Key"
                            placeholder="Enter token"
                            icon={<i className="fa-brands fa-telegram"></i>}
                            value={connectionSecrets?.zendesk_ticket_key}
                            onChange={(id, value) => updateSecrets({ zendesk_ticket_key: value })}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {connectionType === 'INTERCOM' && (
                    <>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Intercom Region</div>
                          <div className="small-font">
                            Region where the Intercom workspace is hosted
                          </div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomSelect
                            id="intercom-region"
                            label="Intercom Region"
                            options={intercomRegions}
                            value={intercomRegion || 'us'}
                            onChange={(value) => setIntercomRegion(value)}
                            multiSelect={false}
                            disabled={isDisabled}
                          />
                        </div>
                      </div>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Intercom Access Token</div>
                          <div className="small-font">Enter Intercom Access Token</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="intercom-access-token"
                            label="Intercom Access Token"
                            placeholder="Enter access token"
                            icon={<i className="fa-solid fa-key"></i>}
                            value={connectionSecrets?.intercom_access_token}
                            onChange={(id, value) =>
                              updateSecrets({ intercom_access_token: value })
                            }
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Intercom Client ID</div>
                          <div className="small-font">Enter Intercom Client ID</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="intercom-client-id"
                            label="Intercom Client ID"
                            placeholder="Enter client ID"
                            icon={<i className="fa-solid fa-id-card"></i>}
                            value={connectionSecrets?.intercom_client_id}
                            onChange={(id, value) => updateSecrets({ intercom_client_id: value })}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Intercom Client Secret</div>
                          <div className="small-font">Enter Intercom Client Secret</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="intercom-client-secret"
                            label="Intercom Client Secret"
                            placeholder="Enter client secret"
                            icon={<i className="fa-solid fa-lock"></i>}
                            value={connectionSecrets?.intercom_client_secret}
                            onChange={(id, value) =>
                              updateSecrets({ intercom_client_secret: value })
                            }
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {connectionType === 'TRELLO' && (
                    <>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Trello API Key</div>
                          <div className="small-font">Enter Trello API Key</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="trello-api-key"
                            label="Trello API Key"
                            placeholder="Enter API key"
                            icon={<i className="fa-solid fa-key"></i>}
                            value={connectionSecrets?.trello_api_key}
                            onChange={(id, value) => updateSecrets({ trello_api_key: value })}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Trello API Token</div>
                          <div className="small-font">Enter Trello API Token</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="trello-api-token"
                            label="Trello API Token"
                            placeholder="Enter API token"
                            icon={<i className="fa-solid fa-lock"></i>}
                            value={connectionSecrets?.trello_api_token}
                            onChange={(id, value) => updateSecrets({ trello_api_token: value })}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {connectionType === 'LIQPAY' && (
                    <>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">LiqPay Public Key</div>
                          <div className="small-font">Enter LiqPay Public Key</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="liqpay-public-key"
                            label="LiqPay Public Key"
                            placeholder="Enter public key"
                            icon={<i className="fa-solid fa-key"></i>}
                            value={connectionSecrets?.liqpay_public_key}
                            onChange={(id, value) => updateSecrets({ liqpay_public_key: value })}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">LiqPay Private Key</div>
                          <div className="small-font">Enter LiqPay Private Key</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="liqpay-private-key"
                            label="LiqPay Private Key"
                            placeholder="Enter private key"
                            icon={<i className="fa-solid fa-lock"></i>}
                            value={connectionSecrets?.liqpay_private_key}
                            onChange={(id, value) => updateSecrets({ liqpay_private_key: value })}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {connectionType === 'JIRA' && (
                    <>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Jira Base URL</div>
                          <div className="small-font">
                            Enter Jira instance base URL (e.g. https://your-domain.atlassian.net)
                          </div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="jira-base-url"
                            label="Jira Base URL"
                            placeholder="Enter Jira base URL"
                            icon={<i className="fa-brands fa-jira"></i>}
                            value={jiraBaseUrl}
                            onChange={(id, value) => setJiraBaseUrl(value)}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Jira Email</div>
                          <div className="small-font">Email associated with the Jira API token</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="jira-email"
                            label="Jira Email"
                            placeholder="Enter Jira email"
                            icon={<i className="fa-solid fa-envelope"></i>}
                            value={connectionSecrets?.jira_email}
                            onChange={(id, value) => updateSecrets({ jira_email: value })}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Jira API Token</div>
                          <div className="small-font">Enter Jira API Token</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="jira-api-token"
                            label="Jira API Token"
                            placeholder="Enter API token"
                            icon={<i className="fa-solid fa-key"></i>}
                            value={connectionSecrets?.jira_api_token}
                            onChange={(id, value) => updateSecrets({ jira_api_token: value })}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {connectionType === 'ZOHO' && (
                    <>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Zoho Client ID</div>
                          <div className="small-font">Self Client / OAuth client ID</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="zoho-client-id"
                            label="Zoho Client ID"
                            placeholder="Enter Client ID"
                            icon={<i className="fa-solid fa-key"></i>}
                            value={connectionSecrets?.zoho_client_id}
                            onChange={(id, value) => updateSecrets({ zoho_client_id: value })}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Zoho Client Secret</div>
                          <div className="small-font">Self Client / OAuth client secret</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="zoho-client-secret"
                            label="Zoho Client Secret"
                            placeholder="Enter Client Secret"
                            icon={<i className="fa-solid fa-lock"></i>}
                            value={connectionSecrets?.zoho_client_secret}
                            onChange={(id, value) => updateSecrets({ zoho_client_secret: value })}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {connectionType === 'USPACY' && (
                    <>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">USPACY URL</div>
                          <div className="small-font">
                            URL, for example https://company.uspacy.ua
                          </div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="uspacy-base-url"
                            label="USPACY URL"
                            placeholder="https://company.uspacy.ua"
                            icon={<i className="fa-solid fa-link"></i>}
                            value={uspacyBaseUrl}
                            onChange={(id, value) => setUspacyBaseUrl(value)}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">USPACY Email</div>
                          <div className="small-font">Enter Email</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="uspacy-email"
                            label="USPACY Email"
                            placeholder="name@example.com"
                            icon={<i className="fa-solid fa-envelope"></i>}
                            value={uspacyEmail}
                            onChange={(id, value) => setUspacyEmail(value)}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">USPACY Password</div>
                          <div className="small-font">Enter uspacy Password</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="uspacy-password"
                            label="USPACY Password"
                            placeholder="Enter password"
                            icon={<i className="fa-solid fa-lock"></i>}
                            value={connectionSecrets?.uspacy_password}
                            onChange={(id, value) => updateSecrets({ uspacy_password: value })}
                            disabled={isDisabled}
                            type="password"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {connectionType === 'CREATIO' && (
                    <>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Creatio base url</div>
                          <div className="small-font">Enter creatio base url</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="creatio-base-url"
                            label="Creatio base url"
                            placeholder="Enter creatio base url"
                            icon={<i className="fa-brands fa-telegram"></i>}
                            value={creatioBaseUrl}
                            onChange={(id, value) => setCreatioBaseUrl(value)}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Creatio token url</div>
                          <div className="small-font">Enter creatio token url</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="creatio-token-url"
                            label="Creatio token url"
                            placeholder="Enter creatio token url"
                            icon={<i className="fa-brands fa-telegram"></i>}
                            value={creatioTokenUrl}
                            onChange={(id, value) => setCreatioTokenUrl(value)}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Creatio client id</div>
                          <div className="small-font">Enter Creatio client id</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="creatio-client-id"
                            label="Creatio Client Id"
                            placeholder="Enter creatio client id"
                            icon={<i className="fa-brands fa-telegram"></i>}
                            value={connectionSecrets?.creatio_client_id}
                            onChange={(id, value) => updateSecrets({ creatio_client_id: value })}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>

                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Creatio client secret</div>
                          <div className="small-font">Enter Creatio client secret</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="creatio-client-secret"
                            label="Creatio Client secret"
                            placeholder="Enter creatio client secret"
                            icon={<i className="fa-brands fa-telegram"></i>}
                            value={connectionSecrets?.creatio_client_secret}
                            onChange={(id, value) =>
                              updateSecrets({ creatio_client_secret: value })
                            }
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Creatio scope</div>
                          <div className="small-font">Enter Creatio scope</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="creatio-scope"
                            label="Creatio scope"
                            placeholder="Enter creatio scope"
                            icon={<i className="fa-brands fa-telegram"></i>}
                            value={creatioScope}
                            onChange={(id, value) => setCreatioScope(value)}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {connectionType === 'KEYCRM' && (
                    <>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">KeyCRM base url</div>
                          <div className="small-font">Enter KeyCRM base url</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="keycrm-base-url"
                            label="KeyCRM base url"
                            placeholder="Enter KeyCRM base url"
                            icon={<i className="fa-brands fa-telegram"></i>}
                            value={keyCrmBaseUrl}
                            onChange={(id, value) => setKeyCrmBaseUrl(value)}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>

                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">KeyCRM api key</div>
                          <div className="small-font">Enter KeyCRM api key</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="keycrm-api-key"
                            label="KeyCRM Api key"
                            placeholder="Enter KeyCRM api key"
                            icon={<i className="fa-brands fa-telegram"></i>}
                            value={connectionSecrets?.key_crm_api_key}
                            onChange={(id, value) => updateSecrets({ key_crm_api_key: value })}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {connectionType === 'MCP' && (
                    <div className="input-block d-flex flex-row justify-between">
                      <div className="input-description">
                        <div className="input-title">MCP base url</div>
                        <div className="small-font">Enter MCP base url</div>
                      </div>
                      <div className="align-wrapper d-flex align-center">
                        <CustomInput
                          id="mcp-base-url"
                          label="MCP base url"
                          placeholder="Enter Mcp base url"
                          icon={<i className="fa-brands fa-telegram"></i>}
                          value={mcpBaseUrl}
                          onChange={(id, value) => setMcpBaseUrl(value)}
                          disabled={isDisabled}
                          type="text"
                          maxLengthValue={2048}
                        />
                      </div>
                    </div>
                  )}
                  {connectionType === 'API' && (
                    <div className="input-block d-flex flex-row justify-between">
                      <div className="input-description">
                        <div className="input-title">API base url</div>
                        <div className="small-font">Enter API base url</div>
                      </div>
                      <div className="align-wrapper d-flex align-center">
                        <CustomInput
                          id="api-base-url"
                          label="API base url"
                          placeholder="Enter API base url"
                          icon={<i className="fa-brands fa-telegram"></i>}
                          value={apiBaseUrl}
                          onChange={(id, value) => setApiBaseUrl(value)}
                          disabled={isDisabled}
                          type="text"
                        />
                      </div>
                    </div>
                  )}

                  {connectionType === 'A2A' && (
                    <div className="input-block d-flex flex-row justify-between">
                      <div className="input-description">
                        <div className="input-title">Assistant</div>
                        <div className="small-font">Select assistant</div>
                      </div>
                      <div className="align-wrapper d-flex align-center">
                        <CustomSelect
                          id="a2a-assistant-key"
                          label="Assistant"
                          options={agents.map((a) => ({ value: a.key, label: a.name }))}
                          value={assistantKey}
                          multiSelect={false}
                          onChange={(val) => setAssistantKey(val)}
                          disabled={isDisabled}
                        />
                      </div>
                    </div>
                  )}

                  {['GOOGLE_DRIVE', 'GOOGLE_CALENDAR'].includes(connectionType) &&
                    !isNew &&
                    (!isGoogleConnected ? (
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Authorization window</div>
                          <div className="small-font">
                            Log in with Google to connect your {connectionType}
                          </div>
                        </div>
                        <div className="button-container d-flex justify-end">
                          <Button
                            variant="primary"
                            size="large"
                            onClick={() => {
                              localStorage.setItem('connectionId', connectionId);
                              localStorage.setItem('connectionOrgId', organizationId);
                              console.log(googleAuthUrl);
                              window.location.href = googleAuthUrl;
                            }}
                          >
                            <i className="fa-brands fa-google" style={{ marginRight: '8px' }}></i>
                            Connect Google account
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Google account</div>
                          <div className="small-font">Connected</div>
                        </div>
                        <div className="button-container d-flex justify-end">
                          <Button
                            variant="danger"
                            size="large"
                            onClick={async () => {
                              try {
                                setLoading(true);
                                await disconnectGoogleAccount(connectionId);
                                setIsGoogleConnected(false);
                                showAlert('success', 'Google account disconnected successfully');
                              } catch (err) {
                                console.error('Failed to disconnect Google account:', err);
                                showAlert('fail', 'Failed to disconnect Google account');
                              } finally {
                                setLoading(false);
                              }
                            }}
                          >
                            <i className="fa-brands fa-google" style={{ marginRight: '8px' }}></i>
                            Disconnect Google account
                          </Button>
                        </div>
                      </div>
                    ))}

                  {connectionType === 'ZOHO' &&
                    !isNew &&
                    (!isZohoConnected ? (
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Authorization window</div>
                          <div className="small-font">Authorize with Zoho to connect to CRM</div>
                        </div>
                        <div className="button-container d-flex justify-end">
                          <Button
                            variant="primary"
                            size="large"
                            onClick={() => {
                              if (!zohoAuthUrl) {
                                showAlert('fail', 'Save Client ID and Client Secret first');
                                return;
                              }
                              localStorage.setItem('connectionId', connectionId);
                              localStorage.setItem('connectionOrgId', organizationId);
                              window.location.href = zohoAuthUrl;
                            }}
                          >
                            <i
                              className="fa-solid fa-right-to-bracket"
                              style={{ marginRight: '8px' }}
                            ></i>
                            Connect Zoho account
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Zoho account</div>
                          <div className="small-font">Connected</div>
                        </div>
                        <div className="button-container d-flex justify-end">
                          <Button
                            variant="danger"
                            size="large"
                            onClick={async () => {
                              try {
                                setLoading(true);
                                await disconnectZohoAccount(connectionId);
                                setIsZohoConnected(false);
                                showAlert('success', 'Zoho account disconnected successfully');
                              } catch (err) {
                                console.error('Failed to disconnect Zoho account:', err);
                                showAlert('fail', 'Failed to disconnect Zoho account');
                              } finally {
                                setLoading(false);
                              }
                            }}
                          >
                            <i
                              className="fa-solid fa-right-from-bracket"
                              style={{ marginRight: '8px' }}
                            ></i>
                            Disconnect Zoho account
                          </Button>
                        </div>
                      </div>
                    ))}

                  {connectionType === 'EMAIL' && (
                    <>
                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">SMTP Address</div>
                          <div className="small-font">Enter SMTP Address</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="smtp-address"
                            label="SMTP Address"
                            placeholder="Enter SMTP Address"
                            icon={<i className="fa-solid fa-paper-plane"></i>}
                            value={smtpAddress}
                            onChange={(id, value) => setSmtpAddress(value)}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>

                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">SMTP Port</div>
                          <div className="small-font">Enter SMTP Port</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="smtp-port"
                            label="SMTP Port"
                            placeholder="Enter SMTP Port"
                            icon={<i className="fa-solid fa-hashtag"></i>}
                            value={smtpPort}
                            onChange={(id, value) => setSmtpPort(value)}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>

                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">IMAP Address</div>
                          <div className="small-font">Enter IMAP Address</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="imap-address"
                            label="IMAP Address"
                            placeholder="Enter IMAP Address"
                            icon={<i className="fa-solid fa-inbox"></i>}
                            value={imapAddress}
                            onChange={(inputIdentifier, inputValue) => setImapAddress(inputValue)}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>

                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">IMAP Port</div>
                          <div className="small-font">Enter IMAP Port</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="imap-port"
                            label="IMAP Port"
                            placeholder="Enter IMAP Port"
                            icon={<i className="fa-solid fa-hashtag"></i>}
                            value={imapPort}
                            onChange={(inputIdentifier, inputValue) => setImapPort(inputValue)}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>

                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Email</div>
                          <div className="small-font">Enter email address</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="email"
                            label="Email"
                            placeholder="Enter email address"
                            icon={<i className="fa-solid fa-envelope"></i>}
                            value={email}
                            onChange={(id, value) => setEmail(value)}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>

                      <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                          <div className="input-title">Password</div>
                          <div className="small-font">Enter email password</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                          <CustomInput
                            id="email-password"
                            label="Password"
                            placeholder="Enter password"
                            icon={<i className="fa-solid fa-lock"></i>}
                            value={connectionSecrets?.email_password}
                            onChange={(id, value) => updateSecrets({ email_password: value })}
                            disabled={isDisabled}
                            type="text"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {connectionType === 'MCP' && (
                    <div className="input-row input-block d-flex flex-row justify-between">
                      <div className="input-description">
                        <div className="input-title">Auto tool sync</div>
                        <div className="small-font">Automatically synchronize MCP tools</div>
                      </div>
                      <div className="align-wrapper d-flex align-center">
                        <CustomToggle
                          id="toggle-auto-sync"
                          label={autoMcpToolSync ? 'Active' : 'Inactive'}
                          checked={autoMcpToolSync}
                          onChange={(id, value) => setAutoMcpToolSync(value)}
                          disabled={false}
                        />
                      </div>
                    </div>
                  )}

                  <div className="input-row input-block d-flex flex-row justify-between">
                    <div className="input-description">
                      <div className="input-title">Activate connection</div>
                      <div className="small-font">
                        Set connection to be active or inactive to enable or disable it
                      </div>
                    </div>
                    <div className="align-wrapper d-flex align-center">
                      <CustomToggle
                        id="toggle-status"
                        label={isActive ? 'Active' : 'Inactive'}
                        checked={isActive}
                        onChange={(id, value) => setIsActive(value)}
                        disabled={false}
                      />
                    </div>
                  </div>
                </div>

                {connectionType === 'MCP' && (
                  <ConnectionHeadersAttributes
                    headerAttributes={headerAttributes}
                    setHeaderAttributes={setHeaderAttributes}
                    canEdit={!isDisabled}
                  />
                )}

                {connectionType === 'MCP' && (
                  <ConnectionParameterAttributes
                    parameterAttributes={parameterAttributes}
                    setParameterAttributes={setParameterAttributes}
                    canEdit={!isDisabled}
                  />
                )}

                {connectionType === 'API' && (
                  <ConnectionHeadersAttributes
                    headerAttributes={headerAttributes}
                    setHeaderAttributes={setHeaderAttributes}
                    canEdit={!isDisabled}
                  />
                )}

                <div className="buttons-container d-flex justify-end">
                  <Button variant="primary" size="medium" disabled={isDisabled} onClick={onSave}>
                    Save
                  </Button>
                  <Button variant="danger" size="medium" onClick={onCancel}>
                    Cancel
                  </Button>
                </div>
              </>
            )}

            {activeTab === 'view-objects' && !isNew && (
              <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
                <ConnectionObjectsList
                  connectionName={connectionName}
                  connectionType={connectionType}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Connection({ canEdit }) {
  return (
    <ConfirmationProvider>
      <AgentApiProvider>
        <ConnectionApiProvider>
          <ConnectionObjectApiProvider>
            <ConnectionDetail canEdit={canEdit} />
          </ConnectionObjectApiProvider>
        </ConnectionApiProvider>
      </AgentApiProvider>
    </ConfirmationProvider>
  );
}

export default Connection;
